import React from 'react'
import { graphql } from 'gatsby'
import Img from 'gatsby-image'
import tw, { styled } from 'twin.macro'
import { LocaleContext } from '../components/layout'
import background from '../images/bluehexagon_faded.svg'
import KeyBenefits from '../components/solutions/key-benefits'
import WatchElementAnimate from '../components/watch-element-animate'
import Help from '../components/services/help'
import SolutionTestimonial from '../components/solutions/solution-testimonial'
import Approach from '../components/services/approach'
import AIWork from '../components/services/ai-work'
import SEO from '../components/SEO/SEO'
import { withPreview } from 'gatsby-source-prismic'
import LocalizedLink from '../components/localized-link'
import { LargeSolidTurquoiseButton } from '../utils/styles'

// styles
const HeroWrapper = styled.div`
  background-image: url(${background});
  background-repeat: no-repeat;
  background-size: 1230px 1405px;
  padding: 138px 0;
  background-position: 200% 68%;
  @media (max-width: 640px) {
    background-size: 120% 100%;
    background-position: 100% 20%;
    padding-bottom: 30px;
  }
`
// markup
const ServicesPage = ({
  data: { prismicServices },
  pageContext: { locale },
}) => {
  const lang = React.useContext(LocaleContext)
  const i18n = lang.i18n[lang.locale]
  return (
    <main>
      <SEO
        title={prismicServices.data.meta_title}
        desc={prismicServices.data.meta_description}
        locale={locale}
      />
      <HeroWrapper>
        <div tw="container">
          <div tw="grid md:grid-cols-2 md:gap-28">
            <div tw="whitespace-normal">
              <span tw="text-teal text-sm tracking-widest font-bold uppercase">
                {prismicServices.data.top_headline}
              </span>
              <div
                dangerouslySetInnerHTML={{
                  __html: prismicServices.data.hero_headline.html,
                }}
                tw="text-navy"></div>
              <p tw="text-xl text-ocean py-8">
                {prismicServices.data.hero_subheadline.text}
              </p>
              <LocalizedLink to="/request-meeting" tw="hidden md:block">
                <LargeSolidTurquoiseButton tw="max-w-sm">
                  {prismicServices.data.hero_button}
                </LargeSolidTurquoiseButton>
              </LocalizedLink>
            </div>
            <div tw="mt-4 md:mt-0">
              <Img
                fluid={prismicServices.data.hero_image.fluid}
                objectFit="contain"
              />
            </div>
            <div tw="md:hidden mt-12">
              <LocalizedLink to="/request-meeting">
                <LargeSolidTurquoiseButton tw="max-w-sm">
                  Let’s talk about the project
                </LargeSolidTurquoiseButton>
              </LocalizedLink>
            </div>
          </div>
        </div>
      </HeroWrapper>
      {prismicServices.data.body.map((b, index) => {
        switch (b.slice_type) {
          case 'key_benefits':
            return <KeyBenefits benefits={b.primary} items={b.items} />
          case 'ways_we_can_help_you':
            return (
              <Help
                help={b.primary}
                items={b.items}
                title={i18n.services.title}
              />
            )
          case 'client_testimonial':
            return (
              <WatchElementAnimate
                className={`.services__animated__${index}`}
                animateName={'animate__fadeInUp'}>
                <div className={`services__animated__${index}`}>
                  <SolutionTestimonial testimonial={b.primary} />
                </div>
              </WatchElementAnimate>
            )
          case 'our_approach':
            return (
              <WatchElementAnimate
                className={`.services__animated__${index}`}
                animateName={'animate__fadeInUp'}>
                <div className={`services__animated__${index}`}>
                  <Approach approach={b.primary} items={b.items} />
                </div>
              </WatchElementAnimate>
            )
          case 'lets_make_ai_work':
            return (
              <AIWork ai={b.primary} items={b.items} flow={'row'} i18n={i18n} />
            )
          default:
            return null
        }
      })}
    </main>
  )
}

export default withPreview(ServicesPage)

export const pageQuery = graphql`
  query ServicesQuery($locale: String!) {
    prismicServices(lang: { eq: $locale }) {
      data {
        meta_title
        meta_description
        top_headline
        hero_button
        body {
          ... on PrismicServicesBodyKeyBenefits {
            slice_type
            primary {
              headline {
                text
              }
              subheadline {
                text
              }
            }
            items {
              card_description
              card_headline
              learn_more {
                url
                slug
              }
              card_icon {
                fluid {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
          }
          ... on PrismicServicesBodyWaysWeCanHelpYou {
            slice_type
            primary {
              ways_description
              headline {
                text
              }
            }
            items {
              way_description
              way_headline
              way_icon {
                fluid {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
          }
          ... on PrismicServicesBodyClientTestimonial {
            primary {
              name
              role
              text
              image {
                fluid {
                  ...GatsbyPrismicImageFluid
                }
              }
              link {
                link_type
                type
                lang
                uid
              }
              button
            }
            slice_type
          }
          ... on PrismicServicesBodyOurApproach {
            slice_type
            primary {
              headline {
                text
              }
              subheadline {
                text
              }
              image {
                fluid {
                  ...GatsbyPrismicImageFluid
                }
              }
              mobile_image {
                fluid {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
            items {
              approach_description
              approach_headline
            }
          }
          ... on PrismicServicesBodyLetsMakeAiWork {
            slice_type
            primary {
              subheadline {
                text
              }
              headline {
                text
              }
            }
            items {
              description {
                html
              }
              icon {
                fluid {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
          }
        }
        hero_headline {
          html
        }
        hero_subheadline {
          text
        }
        hero_image {
          fluid {
            ...GatsbyPrismicImageFluid
          }
        }
      }
    }
  }
`
